import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, alpha } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { School, Verified } from '@mui/icons-material';
import { getFeeStructure } from '../services/api';
import { useTheme } from '@mui/material/styles';

const feesFeatures = [
  { icon: <Verified sx={{ color: 'success.main' }} />, text: 'No Hidden Charges - 100% Transparent Fee Structure' },
  { icon: <Verified sx={{ color: 'success.main' }} />, text: 'EMI & Education Loan Assistance Available' },
  { icon: <Verified sx={{ color: 'success.main' }} />, text: 'Scholarship Opportunities for Meritorious Students' },
  { icon: <Verified sx={{ color: 'success.main' }} />, text: 'Free Counselling & Application Support' },
];

const stateEmojis = {
  'West Bengal': '🏛️',
  'Bihar': '📚',
  'Jharkhand': '⛰️',
  'Uttar Pradesh': '🕌',
  'Karnataka': '🐘',
};

const FeesStructurePage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [statesData, setStatesData] = useState({});
  const [expandedState, setExpandedState] = useState(null);

  useEffect(() => {
    fetchFeeData();
  }, []);

  const fetchFeeData = async () => {
    setLoading(true);
    try {
      const response = await getFeeStructure();
      const colleges = response.data.data || [];
      
      // Group by state
      const grouped = {};
      colleges.forEach((college) => {
        const state = college.state || 'Other';
        if (!grouped[state]) {
          grouped[state] = [];
        }
        grouped[state].push(college);
      });
      
      setStatesData(grouped);
    } catch (error) {
      console.error('Failed to fetch fee structure:', error);
      setStatesData({});
    } finally {
      setLoading(false);
    }
  };

  const toggleState = (state) => {
    setExpandedState(expandedState === state ? null : state);
  };

  const renderFeesCell = (fees) => {
    if (!fees || fees === '') return <Typography variant="body2" color="text.disabled">—</Typography>;
    if (fees === 'NOT FIXED') return (
      <Chip label="Not Fixed" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
    );
    return <Typography sx={{ fontWeight: 600 }}>{fees}</Typography>;
  };

  const renderBedCell = (bed) => {
    if (!bed || bed === '') return <Typography variant="body2" color="text.disabled">—</Typography>;
    if (bed === 'GOVT. HOSPITAL') return (
      <Chip label="Govt. Hospital" size="small" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
    );
    return <Typography sx={{ fontWeight: 500 }}>{bed}</Typography>;
  };

  const states = Object.keys(statesData).sort();

  return (
    <>
      <Helmet>
        <title>MBBS Fees Structure 2026 - Learn Rabbit</title>
        <meta
          name="description"
          content="Comprehensive MBBS fee structure for private medical colleges across Indian states. Transparent fee details for 2026-27 academic year."
        />
      </Helmet>

      <Box sx={{ scrollMarginTop: 100 }}>
        {/* Header */}
        <Box sx={{ py: 8, bgcolor: 'primary.main', color: 'white' }}>
          <Container maxWidth="xl">
            <Typography variant="h2" align="center" sx={{ mb: 4 }}>
              MBBS Fees <Box component="span" sx={{ color: '#D4A853' }}>Structure 2026</Box>
            </Typography>
            <Typography variant="h6" align="center" sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
              Comprehensive fee details for private medical colleges across Indian states (2026-27 academic year)
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ py: 8 }}>
          {/* Key Features */}
          <Grid container spacing={2} sx={{ mb: 6 }}>
            {feesFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: 'background.default',
                  }}
                >
                  {feature.icon}
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {feature.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {loading ? (
            /* Loading Skeleton */
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">Loading fee structure...</Typography>
            </Box>
          ) : states.length === 0 ? (
            /* Empty State */
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <School sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No fee data available
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Fee structure information will appear here once added by the admin.
              </Typography>
            </Box>
          ) : (
            /* State-wise Fee Tables */
            states.map((state) => {
              const colleges = statesData[state] || [];
              const emoji = stateEmojis[state] || '🏛️';
              return (
                <Box key={state} sx={{ mb: 4 }}>
                  <Paper
                    elevation={0}
                    onClick={() => toggleState(state)}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: expandedState === state ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      },
                      bgcolor: expandedState === state ? alpha(theme.palette.primary.main, 0.02) : 'white',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h3" sx={{ fontSize: '2rem' }}>{emoji}</Typography>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {state}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {colleges.length} college{colleges.length > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                          {expandedState === state ? 'Click to collapse' : 'Click to view details'}
                        </Typography>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: expandedState === state ? 'primary.main' : 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            transform: expandedState === state ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        >
                          <Typography sx={{ color: expandedState === state ? 'white' : 'text.secondary', fontSize: 18 }}>
                            ▼
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>

                  {expandedState === state && (
                    <Box sx={{ mt: 2, overflow: 'hidden', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                      <TableContainer>
                        {state === 'Karnataka' ? (
                          /* Karnataka KEA-Style Table */
                          <Table>
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '8%' }}>Code</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '28%' }}>College Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '12%' }}>Type</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '12%' }}>Govt (G)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '12%' }}>Private (P)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '14%' }}>Other (Q)</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '14%' }}>NRI (N)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {colleges.map((college, idx) => (
                                <TableRow
                                  key={college._id || idx}
                                  sx={{
                                    '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>
                                    {college.collegeCode || '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.name}
                                    {college.city && college.city !== state && (
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                        {college.city}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={college.collegeType || '—'}
                                      size="small"
                                      color={
                                        college.collegeType === 'Government' ? 'success' :
                                        college.collegeType === 'Deemed University' ? 'info' :
                                        college.collegeType === 'Minority (I)' ? 'warning' : 'default'
                                      }
                                      variant="outlined"
                                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.govtFees > 0 ? `₹${college.govtFees.toLocaleString('en-IN')}` : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.privateFees > 0 ? `₹${college.privateFees.toLocaleString('en-IN')}` : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.otherFees > 0 ? `₹${college.otherFees.toLocaleString('en-IN')}` : '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.nriFees > 0 ? `₹${college.nriFees.toLocaleString('en-IN')}` : '—'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          /* Standard Table for other states */
                          <Table>
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '35%' }}>College Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '22%' }}>Total Fees</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '22%' }}>Proposed Fees</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 700, width: '21%' }}>Hospital Bed</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {colleges.map((college, idx) => (
                                <TableRow
                                  key={college._id || idx}
                                  sx={{
                                    '&:nth-of-type(even)': { bgcolor: 'action.hover' },
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600 }}>
                                    {college.name}
                                    {college.city && college.city !== state && (
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                        {college.city}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>{renderFeesCell(college.totalFees)}</TableCell>
                                  <TableCell>{renderFeesCell(college.proposedFees)}</TableCell>
                                  <TableCell>{renderBedCell(college.hospitalBed)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              );
            })
          )}

          {/* Note */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: '1px dashed',
              borderColor: 'primary.light',
              mt: 4,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Note:</strong> The fees mentioned above are for the MBBS program (2026-27 academic year) and may be subject to change. 
              "Proposed" fees indicate the revised fee structure under consideration. Colleges marked with "NOT FIXED" have not yet finalized their fee structure.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              For the most accurate and up-to-date information, please <a href="/#contact" style={{ color: '#1976d2', fontWeight: 600 }}>contact our counselors</a> for a personalized fee breakdown and admission guidance.
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default FeesStructurePage;